import type { VariantPickerListItem } from 'autogen/swagger/all';
import SplashRenderer from 'components/shared/Splash/SplashRenderer';
import { useContext } from 'react';
import styled, { css } from 'styled-components';
import { Typography } from 'components/generic/typography';
import { em } from 'modules/helpers/style';
import { ColorNeutralDarkGrey, Spacing16 } from 'autogen/design-tokens/tokens';
import SwatchRenderer from '../../Swatches/SwatchRenderer';
import { SelectionCallbackContext } from '../shared/context/selectionCallback';
import { selectedStyling } from '../../Swatches/shared/styles';
import type { ColorStyleProps } from '../../Swatches/shared/types';

// #region Styles
const Label = styled.label`
    display: flex;
    margin: 0;
`;

const Input = styled.input`
    opacity: 0;
    width: 0;
    height: 0;
`;

const Content = styled.div`
    display: flex;
    align-items: center;
    flex: 0 0 100%;
    gap: ${Spacing16};
`;

const SubText = styled.span`
    font-size: ${em(10, 12)};
    color: ${ColorNeutralDarkGrey};
`;

const StyledSplashRenderer = styled(SplashRenderer)`
    margin-left: auto;
    flex-shrink: 0;
`;

const StyledSwatchRenderer = styled(SwatchRenderer)<{ $size: ColorStyleProps['$size'] }>`
    flex-shrink: 0;

    ${Input}:focus + div > & {
        ${({ $size }) => selectedStyling($size)}
    }
`;

const Name = styled(Typography.Body)<{ $unavailable: boolean }>`
    ${({ $unavailable }) =>
        $unavailable &&
        css`
            opacity: 0.5;
        `}
`;
// #endregion Styles

type VerticalItemProps = {
    item: VariantPickerListItem;
    radioGroupName: string;
};

export default function VerticalItem({ item, radioGroupName }: VerticalItemProps): JSX.Element {
    const { name, isSelected, splash, unitPrice, category, unavailable, displayType } = item;
    const onCheckedCallback = useContext(SelectionCallbackContext);
    const showUnitPrice = unitPrice && category !== 'Color';

    return (
        <Label>
            <Input
                type="radio"
                name={radioGroupName}
                checked={isSelected}
                onChange={() => {
                    if (onCheckedCallback) {
                        onCheckedCallback(item.productId);
                    }
                }}
            />
            <Content>
                <StyledSwatchRenderer
                    {...{ ...item, linkAction: undefined, size: displayType === 'Image' ? 'sm' : 'lg' }}
                    $size="lg"
                />

                <Name as="span" $unavailable={unavailable}>
                    {name}
                    {showUnitPrice && <SubText>&nbsp;{unitPrice}</SubText>}
                </Name>

                {splash && <StyledSplashRenderer splash={splash} />}
            </Content>
        </Label>
    );
}
